import { ReactNode } from "react";
import { Autocomplete, Box, Button, ButtonProps, Chip, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import Iconify from "src/components/iconify";
import { METRIC_KEYS, METRICS, MetricKey } from "./utils";
import { tr } from "./i18n";

// Figma "Signal 2026 - Q3 / Final - Bid Strat testing" tokens
export const COLORS = {
    // Lot 3: "Scheduled" badge and "Cancel Schedule" button
    scheduled: "#1E88E5",
    scheduledBg: "rgba(30, 136, 229, 0.1)",
    danger: "#B71D18",
    dangerBg: "#FFE9D5",
    title: "#1C252E",
    text: "#333C47",
    secondary: "#637381",
    muted: "#919EAB",
    border: "rgba(145, 158, 171, 0.2)",
    headerBg: "rgba(226, 229, 233, 0.16)",
    link: "rgba(0, 108, 156, 0.85)",
    positive: "#0DB46B",
    negative: "#E03535",
    black: "#181818",
};

export type IconName =
    | "alert-triangle"
    | "arrow-right"
    | "arrow-up-right"
    | "check"
    | "chevron-down"
    | "chevron-left"
    | "chevron-right"
    | "chevron-right-small"
    | "columns"
    | "download-01"
    | "filter"
    | "info-circle"
    | "info-circle-active"
    | "loading-02"
    | "selector-open"
    | "trash-04"
    | "x-close";

/** Figma icons, kept with their own size and colour (public/assets/icons/bid-strat-testing). */
export function Icon({ name, size, sx }: { name: IconName; size: number; sx?: object }) {
    return (
        <Box
            component="img"
            src={`/assets/icons/bid-strat-testing/${name}.svg`}
            alt=""
            sx={{ width: size, height: size, display: "block", flexShrink: 0, ...sx }}
        />
    );
}

// Backdrop of the modals (Figma, bug report #16): light, with a 3px blur
export const BACKDROP_SX = { bgcolor: "rgba(22, 28, 36, 0.2)", backdropFilter: "blur(3px)" };

export const CARD_SX = {
    bgcolor: "white",
    border: "1px solid #EAEAEA",
    borderRadius: "12px",
    boxShadow: "0px 6px 12px 0px rgba(145, 158, 171, 0.06)",
    overflow: "hidden",
};

/** "ONGOING EXPERIMENTS (03)" bar of a block */
export function BlockHeader({ title, action }: { title: string; action?: ReactNode }) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{ height: 44, px: 2, borderBottom: `1px solid ${COLORS.border}` }}
        >
            <Typography
                sx={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: COLORS.secondary,
                    letterSpacing: "0.28px",
                    textTransform: "uppercase",
                }}
            >
                {title}
            </Typography>
            {action}
        </Stack>
    );
}

export function BlackButton({ sx, ...props }: ButtonProps) {
    return (
        <Button
            disableElevation
            variant="contained"
            {...props}
            sx={{
                bgcolor: COLORS.black,
                color: "white",
                fontSize: 13,
                fontWeight: 600,
                lineHeight: "18px",
                px: 1.5,
                py: 0.75,
                minWidth: 0,
                borderRadius: "8px",
                "&:hover": { bgcolor: "#000" },
                ...sx,
            }}
        />
    );
}

/** Light red button of the design ("Cancel Schedule", lot 3) */
export function DangerButton({ sx, ...props }: ButtonProps) {
    return (
        <Button
            disableElevation
            {...props}
            sx={{
                bgcolor: COLORS.dangerBg,
                color: COLORS.danger,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: "18px",
                px: 1.5,
                py: 0.75,
                minWidth: 0,
                borderRadius: "8px",
                "&:hover": { bgcolor: "#FFD9BD" },
                ...sx,
            }}
        />
    );
}

/** "Scheduled – DD/MM" badge next to the start date (lot 3, review ticket) */
export function ScheduledBadge({ startDate }: { startDate: string }) {
    const [, month, day] = startDate.split("-");
    return (
        <Box
            component="span"
            sx={{
                px: 1,
                py: 0.25,
                borderRadius: "12px",
                bgcolor: COLORS.scheduledBg,
                color: COLORS.scheduled,
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.4,
                whiteSpace: "nowrap",
                flexShrink: 0,
            }}
        >
            {tr("table.scheduled_on", { date: `${day}/${month}` })}
        </Box>
    );
}

export function GreyButton({ sx, ...props }: ButtonProps) {
    return (
        <Button
            disableElevation
            {...props}
            sx={{
                bgcolor: "rgba(145, 158, 171, 0.16)",
                color: COLORS.text,
                fontSize: 13,
                fontWeight: 500,
                px: 1.5,
                py: 0.5,
                borderRadius: "8px",
                "&:hover": { bgcolor: "rgba(145, 158, 171, 0.28)" },
                ...sx,
            }}
        />
    );
}

/** Centered title + subtitle, as in the empty and blocked states */
export function StateMessage({ title, description }: { title: ReactNode; description?: ReactNode }) {
    return (
        <Stack alignItems="center" gap={0.25} textAlign="center">
            <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2, color: COLORS.title }}>{title}</Typography>
            {description && (
                <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.2, color: COLORS.secondary }}>
                    {description}
                </Typography>
            )}
        </Stack>
    );
}

/** Graph area placeholder: loading, insufficient data, error */
export function DashedArea({ children }: { children: ReactNode }) {
    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={{ height: 260, border: "1px dashed rgba(0, 108, 156, 0.5)", borderRadius: "12px", bgcolor: "white" }}
        >
            {children}
        </Stack>
    );
}

// Same selector as the rest of the platform (CustomToolbar of the activation grids):
// MUI Autocomplete, the selected value in a grey chip, the theme's popup chevron
const CHIP_SX = {
    height: 24,
    borderRadius: "6px",
    fontSize: 13,
    fontWeight: 500,
    bgcolor: "rgba(145, 158, 171, 0.32)",
    color: "rgba(51, 60, 71, 1)",
    "&:hover": { bgcolor: "rgba(145, 158, 171, 0.50)" },
    "& .MuiChip-icon": { ml: "8px", mr: "-2px" },
};

function ChipAutocomplete<T extends string>({
    value,
    options,
    onChange,
    getLabel,
    label,
    ariaLabel,
    width,
    dot,
    action,
    chip = true,
}: {
    value: T;
    options: T[];
    onChange: (value: T) => void;
    getLabel: (value: T) => string;
    // Floating label on the border ("Metric 1", "Group by")
    label?: string;
    ariaLabel: string;
    width: number;
    // Colour dot of a metric, in the chip and the options
    dot?: (value: T) => string;
    // Button before the chevron (show / hide the series)
    action?: ReactNode;
    // false: the value as plain text (Metric 1 / Metric 2, Lily)
    chip?: boolean;
}) {
    const dotIcon = (option: T) =>
        dot ? <Box component="span" sx={{ width: 8, height: 8, borderRadius: "9px", bgcolor: dot(option), flexShrink: 0 }} /> : undefined;
    return (
        <Autocomplete
            size="small"
            disableClearable
            options={options}
            value={value}
            onChange={(_event, option) => option && onChange(option)}
            getOptionLabel={getLabel}
            // With a chip, the chip shows the value and the input stays empty
            inputValue={chip ? "" : getLabel(value)}
            onInputChange={() => undefined}
            sx={{ width }}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option} sx={{ gap: 1, fontSize: 14, fontWeight: 500 }}>
                    {dotIcon(option)}
                    {getLabel(option)}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    inputProps={{ ...params.inputProps, readOnly: true, "aria-label": ariaLabel }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: chip ? (
                            <Chip size="small" icon={dotIcon(value)} label={getLabel(value)} sx={CHIP_SX} />
                        ) : (
                            dot && <Box sx={{ display: "flex", ml: 0.75, mr: 0.25 }}>{dotIcon(value)}</Box>
                        ),
                        endAdornment: (
                            <>
                                {action && (
                                    <Box sx={{ position: "absolute", right: 34, top: "50%", transform: "translateY(-50%)", display: "flex" }}>
                                        {action}
                                    </Box>
                                )}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    sx={{
                        "& .MuiInputBase-root": {
                            height: 36,
                            py: "0 !important",
                            pl: "6px !important",
                            ...(action ? { pr: "64px !important" } : {}),
                            cursor: "pointer",
                        },
                        "& .MuiAutocomplete-input": {
                            p: "0 !important",
                            minWidth: "0 !important",
                            cursor: "pointer",
                            ...(chip ? {} : { pl: "6px !important", fontSize: 14, fontWeight: 500, color: COLORS.text }),
                        },
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(145, 158, 171, 0.3)" },
                        "& .MuiInputLabel-root": { fontSize: 12, fontWeight: 600, color: COLORS.muted },
                        // Beats the theme's shrink override (font.label): the same 12px label as the modal
                        "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                            fontSize: 12,
                            lineHeight: 1.2,
                            fontWeight: 600,
                            transform: "translate(14px, -7px) scale(1)",
                        },
                        // Notch sized for the 12px bold label (MUI default: 0.75em of the input)
                        "& .MuiOutlinedInput-notchedOutline legend": { fontSize: 12, fontWeight: 600 },
                    }}
                />
            )}
        />
    );
}

/** "Metric 1 · ● Brand traffic ⌄" */
export function MetricSelect({
    label,
    value,
    onChange,
    excluded,
    options = METRIC_KEYS,
    hidden = false,
    onToggleHidden,
    cannotHide = false,
}: {
    label: string;
    value: MetricKey;
    onChange: (key: MetricKey) => void;
    // Metric shown by the other selector: the same series cannot be drawn twice
    excluded?: MetricKey;
    // Statistical graph: the 4 metrics of Causal Impact only
    options?: MetricKey[];
    // Eye of the platform's metric selectors (Non-Branded metric evolution): show / hide the series
    hidden?: boolean;
    onToggleHidden?: () => void;
    // The last visible series cannot be hidden
    cannotHide?: boolean;
}) {
    const action = onToggleHidden && (
        <Tooltip title={tr(hidden ? "selectors.show_series" : "selectors.hide_series")} placement="top">
            <span>
                <IconButton
                    size="small"
                    aria-label={tr(hidden ? "selectors.show" : "selectors.hide", { metric: METRICS[value].label })}
                    disabled={cannotHide}
                    // Without opening the list
                    onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onToggleHidden();
                    }}
                    sx={{
                        width: 28,
                        height: 28,
                        p: 0,
                        color: hidden ? "#C4CDD5" : "#91A0AF",
                        "&:hover": { bgcolor: "rgba(145, 158, 171, 0.08)" },
                        "&.Mui-disabled": { color: "#C4CDD5" },
                    }}
                >
                    <Iconify icon={hidden ? "solar:eye-closed-linear" : "solar:eye-linear"} width={20} />
                </IconButton>
            </span>
        </Tooltip>
    );
    return (
        <ChipAutocomplete
            action={action}
            // The value as plain text with its eye, as on the front Lily validated
            chip={false}
            label={label}
            ariaLabel={label}
            value={value}
            options={options.filter((key) => key !== excluded)}
            onChange={onChange}
            getLabel={(key) => METRICS[key].label}
            dot={(key) => METRICS[key].color}
            width={onToggleHidden ? 230 : 200}
        />
    );
}

/** "Group by · Daily ⌄", recap display mode "Weekly ⌄" */
export function PillSelect<T extends string>({
    value,
    options,
    onChange,
    label,
    ariaLabel,
}: {
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
    label?: string;
    ariaLabel: string;
}) {
    const labels = Object.fromEntries(options.map((option) => [option.value, option.label])) as Record<T, string>;
    return (
        <ChipAutocomplete
            label={label}
            ariaLabel={ariaLabel}
            value={value}
            options={options.map((option) => option.value)}
            onChange={onChange}
            getLabel={(option) => labels[option]}
            width={label ? 130 : 120}
        />
    );
}

/** "Descriptive graph ⓘ | Statistical graph ⓘ" (Figma), the selected one on a grey chip */
export function SegmentedToggle<T extends string>({
    value,
    options,
    onChange,
    ariaLabel,
}: {
    value: T;
    options: { value: T; label: string; info: string }[];
    onChange: (value: T) => void;
    ariaLabel: string;
}) {
    return (
        <Stack
            role="group"
            aria-label={ariaLabel}
            direction="row"
            gap={1}
            sx={{ height: 30, p: "3px", border: `1px solid ${COLORS.border}`, borderRadius: "8px", bgcolor: "white" }}
        >
            {options.map((option) => {
                const selected = option.value === value;
                return (
                    <Box
                        key={option.value}
                        component="button"
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onChange(option.value)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            px: 1,
                            border: "none",
                            borderRadius: "6px",
                            bgcolor: selected ? "rgba(145, 158, 171, 0.2)" : "transparent",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: 13,
                            fontWeight: 500,
                            lineHeight: "23px",
                            color: COLORS.text,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {option.label}
                        <Tooltip title={option.info} placement="top">
                            <Box component="span" sx={{ display: "flex" }}>
                                <Icon name={selected ? "info-circle-active" : "info-circle"} size={12} />
                            </Box>
                        </Tooltip>
                    </Box>
                );
            })}
        </Stack>
    );
}
